import { isUndefined } from 'lodash-es';
import React, { ReactElement } from 'react';

import { IUploadConfig } from '~/src/dash/api/resource';
import { Row, Rows } from '~/src/dash/components/layout';
import { Text } from '~/src/dash/components/text';
import { publicaColors } from '~/src/dash/utils/colors';
import UploadManager from '~/src/dash/utils/upload_manager';
import { EXTENSION_TYPE_MP4 } from '~/src/dash/views/networks/network-settings-view-constants';

const generateOneTooltipRowForBetaFeatures = (lineOfText: string): ReactElement => {
  return (
    <Row noVerticalPadding>
      <Text extraSmall color={publicaColors.white}>
        {lineOfText}
      </Text>
    </Row>
  );
};

export const generateTooltipForBetaFeatures = (
  textForTooltipRows: string[],
  tooltipHeader?: string,
): ReactElement => {
  return (
    <Rows>
      {tooltipHeader && (
        <Row noVerticalPadding>
          <Text small bold color={publicaColors.white}>
            {tooltipHeader}
          </Text>
        </Row>
      )}
      {textForTooltipRows.map((lineOfText: string) => {
        return generateOneTooltipRowForBetaFeatures(lineOfText);
      })}
    </Rows>
  );
};

export const potentiallyValidNonPositiveInteger = (inputText: string): boolean => {
  return ['', '-'].includes(inputText) ? true : false;
};

export const canParseAsBase10Integer = (value: string): boolean => {
  return !isNaN(parseInt(value, 10));
};

export const forceNumberInBounds = (
  lowerBound: number,
  upperBound: number,
  value: number,
): number => {
  lowerBound = Math.min(lowerBound, upperBound);
  return Math.min(upperBound, Math.max(lowerBound, value));
};

export const isRangeOfValues = (rangeOrNumber: number | number[]): boolean => {
  return Array.isArray(rangeOrNumber);
};

// Mediafile upload helpers
export interface IUploadSingleMediafileInput {
  changeEvent: React.ChangeEvent<HTMLInputElement>;
  uploadManager: UploadManager;
  uploadConfig: IUploadConfig;
  uploadFunction: (status: IUploadConfig) => Promise<{ status: number; response: any }>;
  setUploadingStatus: (isUploading: boolean) => void;
  setSnackbarErrorMessage: (isError: boolean, errorMessage: string) => void;
  setMediafileURL?: (url: string) => void;
  onSuccessCallback?: (resp: any) => void;
  customName?: string;
}

export const uploadSingleMediafile = (input: IUploadSingleMediafileInput) => {
  // Preprocessing
  const target = input.changeEvent.target;
  if (target.files?.length !== 1) {
    input.setSnackbarErrorMessage(
      true,
      `error: uploader expects exactly one file, got: ${target.files?.length}`,
    );
    return;
  }
  const file = target.files[0];
  const fileNameComponents = file.name.split('.');
  if (fileNameComponents.length < 2) {
    input.setSnackbarErrorMessage(
      true,
      `error: uploader expects uploaded file to have a name, got: ${fileNameComponents?.[0]}`,
    );
    return;
  }
  if (!isSupportedExtensionType(fileNameComponents[fileNameComponents.length - 1])) {
    input.setSnackbarErrorMessage(
      true,
      `error: uploader expects filetype in ${[EXTENSION_TYPE_MP4].toString}, got: ${
        target.files.length
      }`,
    );
    return;
  }

  // Enrich upload configuration
  input.uploadConfig.file = file;
  input.uploadConfig.name = input.customName || fileNameComponents[0];
  input.setUploadingStatus(true);
  const uploadKey = input.uploadManager.addUpload(file.name);
  input
    .uploadFunction(input.uploadConfig)
    .then((result: { status: number; response: any[] }) => {
      if (result.status === 200) {
        try {
          input.uploadManager.signalSuccess(uploadKey);
          if (!isUndefined(input.onSuccessCallback)) {
            input.onSuccessCallback([...result.response]);
          }
        } catch (e) {
          input.uploadManager.signalFailure(uploadKey);
        }
      } else {
        input.uploadManager.signalFailure(uploadKey);
      }
      input.setUploadingStatus(false);
    })
    .catch(() => {
      input.uploadManager.signalFailure(uploadKey);
      input.setUploadingStatus(false);
    });
};

export const isSupportedExtensionType = (extension: string): boolean => {
  return [EXTENSION_TYPE_MP4].includes(extension);
};
